import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faMagnifyingGlass,
  faLocationDot,
  faUserGroup,
  faLayerGroup,
  faUsersGear,
  faCheck,
  faXmark,
  faChevronDown,
  faSitemap,
} from "@fortawesome/free-solid-svg-icons";
import { psGet, psPost } from "./PSApiClient";
import { circleBannerUrl } from "./psImageUrls";
import PSImageUploader from "./PSImageUploader";
import PSChapter from "./PSChapter";
import {
  ui,
  PrimaryButton,
  GhostButton,
  DirectorBadge,
  MemberBadge,
  PendingBadge,
  EmptyState,
  ErrorState,
  Notice,
  EntityThumb,
  BannerImage,
  getRole,
  memberCountLabel,
  memberLabel,
  memberId,
} from "./psShared";

export default function PSCircles({ deepLinkSlug = null }) {
    // const user = useSelector((s) => s.authState?.user);
    const authState = useSelector(state => state.authReducer);
    const user = authState?.user;
  const [subTab, setSubTab] = useState("discover"); // 'discover' | 'mine'
  const [search, setSearch] = useState("");
  const [circles, setCircles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCircleId, setExpandedCircleId] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [formError, setFormError] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", location: "", pinCode: "", state: "" });
  const [creating, setCreating] = useState(false);
  const [pinnedCircle, setPinnedCircle] = useState(null);
  const [pinnedError, setPinnedError] = useState("");
  const [manageOpenFor, setManageOpenFor] = useState(null);
  const [addChapterCode, setAddChapterCode] = useState("");
  const [addingChapter, setAddingChapter] = useState(false);
  const [bannerVersions, setBannerVersions] = useState({});

  const requestSeq = useRef(0);

  useEffect(() => {
    if (!deepLinkSlug) return;
    setPinnedError("");
    psGet(`/circles/slug/${deepLinkSlug}`)
      .then(setPinnedCircle)
      .catch(() => setPinnedError("That circle link looks invalid or the circle was removed."));
  }, [deepLinkSlug]);

  const load = () => {
    const mySeq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    psGet(`/circles${search ? `?search=${encodeURIComponent(search)}` : ""}`)
      .then((data) => {
        if (mySeq !== requestSeq.current) return;
        setCircles(data);
      })
      .catch((e) => {
        if (mySeq !== requestSeq.current) return;
        setError(e.message);
      })
      .finally(() => {
        if (mySeq !== requestSeq.current) return;
        setLoading(false);
      });
  };

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const myCircles = circles.filter((c) => {
    const { isDirector, isMember } = getRole(c, user?._id);
    return isDirector || isMember;
  });

  const handleJoin = async (id) => {
    setError(null);
    setNotice("");
    try {
      await psPost(`/circles/${id}/join-request`, {});
      setNotice("Join request sent — the circle director will approve you.");
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleAffiliationDecision = async (circleId, chapterId, approve) => {
    try {
      await psPost(`/circles/${circleId}/chapters/${chapterId}/decision`, { approve });
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleDeactivateChapter = async (circleId, chapterId) => {
    try {
      await psPost(`/circles/${circleId}/chapters/${chapterId}/deactivate`, {});
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleAddChapter = async (circleId) => {
    if (!addChapterCode.trim()) return;
    setError(null);
    try {
      setAddingChapter(true);
      await psPost(`/circles/${circleId}/chapters/add`, { chapterSlug: addChapterCode.trim() });
      setAddChapterCode("");
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setAddingChapter(false);
    }
  };

  const handleMemberDecision = async (circleId, userId, approve) => {
    try {
      await psPost(`/circles/${circleId}/members/${userId}/${approve ? "approve" : "reject"}`, {});
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError(null);
    try {
      setCreating(true);
      await psPost("/circles", form);
      setForm({ name: "", description: "", location: "", pinCode: "", state: "" });
      setShowForm(false);
      setSubTab("mine");
      setNotice("Circle created — pending our team's activation before it goes live.");
      load();
    } catch (e) {
      // FIX: this is the error that was previously invisible. If it still
      // never appears and the button just sits on "Creating…" forever, that
      // confirms the request never got a response at all — see psRequireMembership.
      setFormError(e.message);
    } finally {
      setCreating(false);
    }
  };

  const listToShow = subTab === "mine" ? myCircles : circles;

  return (
    <div className="p-4 max-w-3xl mx-auto">
      {pinnedError && <ErrorState message={pinnedError} />}

      {pinnedCircle && (
        <div className={`${ui.card} mb-4 overflow-hidden border-fuchsia-400/30`}>
          <BannerImage src={circleBannerUrl(pinnedCircle)} name={pinnedCircle.name} className="h-24 w-full" />
          <div className="p-4">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-fuchsia-300/80">You were invited to</p>
            <h3 className="mt-1 text-base lg:text-lg font-bold text-white">{pinnedCircle.name}</h3>
            <p className="text-[12px] lg:text-sm text-white/50">
              {pinnedCircle.location}{pinnedCircle.state ? `, ${pinnedCircle.state}` : ""}
            </p>
            {pinnedCircle.description && (
              <p className="mt-1 text-[12px] lg:text-sm text-white/60">{pinnedCircle.description}</p>
            )}
            <div className="mt-3 flex items-center justify-between">
              <span className="text-[11px] lg:text-xs text-white/40">{memberCountLabel(pinnedCircle)}</span>
              {(() => {
                const { isDirector, isMember, isPending } = getRole(pinnedCircle, user?._id);
                if (isDirector) return <DirectorBadge />;
                if (isMember) return <MemberBadge />;
                if (isPending) return <PendingBadge />;
                return <PrimaryButton onClick={() => handleJoin(pinnedCircle._id)}>Request to join</PrimaryButton>;
              })()}
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {[
          { id: "discover", label: "Discover" },
          { id: "mine", label: "My Circles" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={[
              "rounded-full w-fit px-3 py-1.5 text-[11px] lg:text-sm font-semibold transition",
              subTab === t.id ? ui.gradientBtn : "border border-white/10 bg-white/[0.03] text-white/60 hover:text-white",
            ].join(" ")}
          >
            {t.label}
          </button>
        ))}
        <GhostButton className="ml-auto" onClick={() => { setShowForm((s) => !s); setFormError(null); }}>
          <FontAwesomeIcon icon={faPlus} className="mr-1 text-[10px]" />
          Start a Circle {user?.name}
        </GhostButton>
      </div>

      <div className="relative mt-3">
        <FontAwesomeIcon icon={faMagnifyingGlass} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[12px] text-white/30" />
        <input
          type="text"
          placeholder="Search circles by name or location…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={`${ui.input} pl-9`}
        />
      </div>

      {notice && <div className="mt-3"><Notice>{notice}</Notice></div>}

      {showForm && (
        <form onSubmit={handleCreate} className={`${ui.card} mt-4 space-y-3 p-4`}>
          <p className="text-[11px] text-white/40">Starting a circle needs an active PehchaanSetu membership.</p>
          {formError && <ErrorState message={formError} />}
          <input required placeholder="Circle name" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={ui.input} />
          <textarea placeholder="Purpose / description" value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2} className={ui.input} />
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Location (e.g. Roha)" value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              className={ui.input} />
            <input placeholder="PIN code" value={form.pinCode}
              onChange={(e) => setForm({ ...form, pinCode: e.target.value })}
              className={ui.input} />
          </div>
          <input placeholder="State" value={form.state}
            onChange={(e) => setForm({ ...form, state: e.target.value })}
            className={ui.input} />
          <PrimaryButton type="submit" disabled={creating}>
            {creating ? "Creating…" : "Create circle"}
          </PrimaryButton>
        </form>
      )}

      <div className="mt-4 grid gap-3">
        {loading &&
          Array.from({ length: 3 }).map((_, i) => <div key={i} className={`${ui.card} h-48 animate-pulse`} />)}

        {!loading && error && <ErrorState message={error} onRetry={load} />}

        {!loading &&
          !error &&
          listToShow.map((c) => {
            const { isDirector, isMember, isPending } = getRole(c, user?._id);
            const pendingMembers = c.pendingRequestIds || [];
            const pendingChapters = c.pendingChapterRequestIds || [];
            const affiliatedChapters = c.memberChapterIds || [];
            const isExpanded = expandedCircleId === c._id;
            const isManaging = manageOpenFor === c._id;

            return (
              <div key={c._id} className={`${ui.card} overflow-hidden`}>
                <div className="relative">
                  <BannerImage src={circleBannerUrl(c, bannerVersions[c._id])} name={c.name} className="h-24 w-full lg:h-28" />
                  {isDirector && (
                    <div className="absolute right-2 top-2 h-9 w-16 overflow-hidden rounded-lg opacity-90">
                      <PSImageUploader
                        uploadUrl="/upload-pehchaansetucircleimage"
                        codeHeader="x-circle-code"
                        code={c.slug}
                        currentUrl={circleBannerUrl(c, bannerVersions[c._id])}
                        shape="banner"
                        onUploaded={() => setBannerVersions((v) => ({ ...v, [c._id]: Date.now() }))}
                      />
                    </div>
                  )}
                </div>

                <div className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="-mt-10">
                      <EntityThumb src={circleBannerUrl(c, bannerVersions[c._id])} name={c.name} shape="circle" size="lg" />
                    </div>
                    <div className="min-w-0 flex-1 pt-1.5">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <h3 className="truncate text-[14px] lg:text-lg font-bold text-white">{c.name}</h3>
                        {isDirector && <DirectorBadge />}
                        {!isDirector && isMember && <MemberBadge />}
                        {!isDirector && !isMember && isPending && <PendingBadge />}
                      </div>
                      <p className="flex items-center gap-1 text-[11px] lg:text-sm text-white/45">
                        <FontAwesomeIcon icon={faLocationDot} className="text-[10px]" />
                        {c.location}{c.state ? `, ${c.state}` : ""}
                      </p>
                    </div>
                  </div>

                  {c.description && (
                    <p className="mt-2 line-clamp-2 text-[12px] lg:text-sm text-white/60">{c.description}</p>
                  )}

                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                    <span className="flex items-center gap-1 text-[11px] lg:text-xs text-white/40">
                      <FontAwesomeIcon icon={faUserGroup} className="text-[10px]" />
                      {memberCountLabel(c)}
                    </span>
                    <div className="flex flex-wrap items-center gap-2">
                      {isDirector && (
                        <GhostButton onClick={() => setManageOpenFor(isManaging ? null : c._id)}>
                          <FontAwesomeIcon icon={faUsersGear} className="mr-1 text-[10px]" />
                          Manage{pendingMembers.length + pendingChapters.length > 0 ? ` (${pendingMembers.length + pendingChapters.length})` : ""}
                        </GhostButton>
                      )}
                      <GhostButton onClick={() => setExpandedCircleId(isExpanded ? null : c._id)}>
                        <FontAwesomeIcon icon={faLayerGroup} className="mr-1 text-[10px]" />
                        {isExpanded ? "Hide chapters" : "View chapters"}
                        <FontAwesomeIcon icon={faChevronDown} className={`ml-1 text-[9px] transition ${isExpanded ? "rotate-180" : ""}`} />
                      </GhostButton>
                      {!isDirector && !isMember && !isPending && (
                        <PrimaryButton onClick={() => handleJoin(c._id)}>Request to join</PrimaryButton>
                      )}
                    </div>
                  </div>

                  {isDirector && isManaging && (
                    <div className="mt-3 space-y-3 rounded-xl border border-white/10 bg-white/[0.03] p-3">
                      {pendingChapters.length > 0 && (
                        <div>
                          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-amber-300/80">
                            Chapters requesting to affiliate
                          </p>
                          <div className="space-y-1.5">
                            {pendingChapters.map((ch) => (
                              <div key={ch._id || ch} className="flex items-center justify-between gap-2">
                                <span className="truncate text-[12px] text-white/75">{ch.name || `Chapter ${String(ch).slice(-6)}`}</span>
                                <div className="flex shrink-0 gap-1.5">
                                  <button onClick={() => handleAffiliationDecision(c._id, ch._id || ch, true)}
                                    className="grid h-7 w-7 place-items-center rounded-full bg-emerald-400/15 text-emerald-300 hover:bg-emerald-400/25" aria-label="Approve">
                                    <FontAwesomeIcon icon={faCheck} className="text-[11px]" />
                                  </button>
                                  <button onClick={() => handleAffiliationDecision(c._id, ch._id || ch, false)}
                                    className="grid h-7 w-7 place-items-center rounded-full bg-rose-400/15 text-rose-300 hover:bg-rose-400/25" aria-label="Decline">
                                    <FontAwesomeIcon icon={faXmark} className="text-[11px]" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {pendingMembers.length > 0 && (
                        <div>
                          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-amber-300/80">
                            People requesting to join
                          </p>
                          <div className="space-y-1.5">
                            {pendingMembers.map((m) => (
                              <div key={String(memberId(m))} className="flex items-center justify-between gap-2">
                                <span className="truncate text-[12px] text-white/75">{memberLabel(m)}</span>
                                <div className="flex shrink-0 gap-1.5">
                                  <button onClick={() => handleMemberDecision(c._id, memberId(m), true)}
                                    className="grid h-7 w-7 place-items-center rounded-full bg-emerald-400/15 text-emerald-300 hover:bg-emerald-400/25" aria-label="Approve">
                                    <FontAwesomeIcon icon={faCheck} className="text-[11px]" />
                                  </button>
                                  <button onClick={() => handleMemberDecision(c._id, memberId(m), false)}
                                    className="grid h-7 w-7 place-items-center rounded-full bg-rose-400/15 text-rose-300 hover:bg-rose-400/25" aria-label="Decline">
                                    <FontAwesomeIcon icon={faXmark} className="text-[11px]" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div>
                        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-white/40">
                          Affiliated chapters ({affiliatedChapters.length})
                        </p>
                        {affiliatedChapters.length === 0 ? (
                          <p className="text-[11px] text-white/35">No chapters affiliated yet.</p>
                        ) : (
                          <div className="space-y-1.5">
                            {affiliatedChapters.map((ch) => (
                              <div key={ch._id || ch} className="flex items-center justify-between gap-2">
                                <span className="truncate text-[12px] text-white/75">
                                  <FontAwesomeIcon icon={faSitemap} className="mr-1.5 text-[10px] text-white/30" />
                                  {ch.name || `Chapter ${String(ch).slice(-6)}`}
                                </span>
                                <button onClick={() => handleDeactivateChapter(c._id, ch._id || ch)}
                                  className="rounded-full border border-rose-400/25 px-2 py-0.5 text-[10px] font-semibold text-rose-300 hover:bg-rose-400/10">
                                  Remove
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div>
                        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-white/40">
                          Add a chapter directly
                        </p>
                        <div className="flex gap-1.5">
                          <input
                            value={addChapterCode}
                            onChange={(e) => setAddChapterCode(e.target.value)}
                            placeholder="Chapter's code"
                            className={`${ui.input} flex-1`}
                          />
                          <GhostButton onClick={() => handleAddChapter(c._id)} disabled={addingChapter}>
                            <FontAwesomeIcon icon={faPlus} className="mr-1 text-[10px]" />
                            Add
                          </GhostButton>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {isExpanded && (
                  <div className="border-t border-white/10 px-4 pb-4">
                    <PSChapter circleId={c._id} />
                  </div>
                )}
              </div>
            );
          })}

        {!loading && !error && listToShow.length === 0 && (
          <EmptyState
            icon={faLayerGroup}
            title={subTab === "mine" ? "You haven't joined or started a circle yet" : "No circles found"}
            hint={subTab === "mine" ? "Discover a circle nearby, or start your own." : "Try a different search, or start one."}
          />
        )}
      </div>
    </div>
  );
}
